import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'

/**
 * Idle logout. After 2 hours with no user interaction (mouse / keyboard / scroll /
 * touch) we show a 60-second "Session expiring" warning, then sign the user out.
 * Any interaction resets the timer, so an active user is never logged out. (Note:
 * user-initiated API calls always follow a DOM interaction, which resets the timer;
 * background polling deliberately does NOT, so it can't keep an idle session alive.)
 */
const IDLE_MS = 2 * 60 * 60 * 1000 // 2h of no interaction → warn
const GRACE_MS = 60 * 1000 // then a 60s warning → logout
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'] as const

export function IdleTimeout() {
  const navigate = useNavigate()
  const clearAuth = useAuthStore((s) => s.clear)
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken && s.user))
  const { t } = useTranslation()
  const lastActivity = useRef(Date.now())
  const [remaining, setRemaining] = useState<number | null>(null) // seconds left in grace, null = no warning

  useEffect(() => {
    if (!isAuthed) return
    const bump = () => { lastActivity.current = Date.now() }
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }))

    const tick = window.setInterval(() => {
      const idle = Date.now() - lastActivity.current
      if (idle >= IDLE_MS + GRACE_MS) {
        window.clearInterval(tick)
        clearAuth()
        navigate('/login?reason=idle', { replace: true })
      } else if (idle >= IDLE_MS) {
        setRemaining(Math.max(0, Math.ceil((IDLE_MS + GRACE_MS - idle) / 1000)))
      } else {
        setRemaining((r) => (r === null ? r : null)) // interaction dismissed the warning
      }
    }, 1000)

    return () => {
      window.clearInterval(tick)
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump))
    }
  }, [isAuthed, clearAuth, navigate])

  if (remaining === null) return null

  const stay = () => { lastActivity.current = Date.now(); setRemaining(null) }
  const signOut = () => { clearAuth(); navigate('/login', { replace: true }) }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/30 px-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-title"
    >
      <div className="w-full max-w-sm rounded-xl border border-hair bg-surface p-6 shadow-pop">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-warn-soft text-warn">
            <Clock className="h-4 w-4" />
          </span>
          <h2 id="idle-title" className="text-title text-ink">
            {t('session.expiringTitle', { defaultValue: 'Session expiring' })}
          </h2>
        </div>
        <p className="mt-3 text-body2 text-ink-body">
          {t('session.expiringBody', { defaultValue: "You've been inactive. You'll be signed out in" })}{' '}
          <span className="font-semibold tabular-nums text-ink">{remaining}s</span>.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={signOut}
            className="rounded-lg px-3.5 py-2 text-caption font-medium text-ink-body transition-colors hover:bg-sunken"
          >
            {t('auth.signOut', { defaultValue: 'Sign out' })}
          </button>
          <button
            onClick={stay}
            className="rounded-lg bg-accent px-3.5 py-2 text-caption font-semibold text-accent-fg transition-colors hover:bg-accent-hover"
          >
            {t('session.stay', { defaultValue: 'Stay signed in' })}
          </button>
        </div>
      </div>
    </div>
  )
}
